import { Component, EventEmitter, Input, Output } from '@angular/core';
import { jobDetails, jobDtlsResponse } from '@interfaces/job-details.model';
import { paginationParams } from '@shared/config/table.config';
import { options, columnDefs } from './ops-job-details.config';
import { Router } from '@angular/router';
import { OpsJobDetailsService } from './ops-job-details.service';
import { GlobalErrorService } from '@services/global-error.service';
import { FileDownloadService } from '@services/file-download.service';
import { JobDetErrorMsg, PersonaType } from '@constants/global.enum';
import { SearchService } from '@services/search.service';
import { HttpErrorResponse } from '@angular/common/http';
import { dateFormat } from '@constants/dateFormat';
import { OpsOpenPositionsService } from '@pages/ops-team/ops-open-positions/ops-open-positions.service';
import { PositionData } from '@interfaces/open-positions.model';

@Component({
  selector: 'app-ops-job-details',
  templateUrl: './ops-job-details.component.html',
  styleUrl: './ops-job-details.component.scss',
})
export class OpsJobDetailsComponent {
  displayData: jobDetails[] = [];
  totalCount: number;
  opened: boolean = false;
  filteredDropdown: jobDetails[] = [];
  paginationParams = paginationParams;
  options = options;
  rowData: jobDetails[] = [];
  columnDefs = columnDefs;
  fieldsVisibility: string = PersonaType.OpsTeamJobDet;
  hiringRequestId: number = 0;
  jobTitle: string = '';
  jobDescriptionFilePath: string = '';
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = JobDetErrorMsg.ErrorMessage;
  filterValue: string = '';
  urlDisplayLimit: number = 15;
  isDataLoaded: boolean = false;
  projectName: string = '';
  primarySkills: string = '';
  hiringManagerName: string = '';
  selectedHiringRequest: any;
  dateFormat = dateFormat;

  @Input() openPositionData: PositionData | null = null;
  @Output() close = new EventEmitter<void>();

  constructor(
    private router: Router,
    public jobDetails: OpsJobDetailsService,
    private globalError: GlobalErrorService,
    private fileDownloadService: FileDownloadService,
    private searchService: SearchService,
    private openPositionsService: OpsOpenPositionsService,
  ) {
    this.totalCount = this.rowData?.length || 0;
  }

  ngOnInit() {
    if (this.openPositionData) {
      this.hiringRequestId = this.openPositionData.hiringRequestId;
      this.jobTitle = this.openPositionData.jobTitle;
      this.jobDescriptionFilePath = this.openPositionData.jobDescriptionPath;
      this.projectName = this.openPositionData.projectName;
      this.hiringManagerName = this.openPositionData.hiringManagerName;
      this.primarySkills = Array.isArray(this.openPositionData.skillName)
        ? this.openPositionData.skillName.join(', ')
        : this.openPositionData.skillName;
    }

    this.openPositionsService.getOpenPositions().subscribe({
      next: (res) => {
        this.selectedHiringRequest = res.data?.find(
          (item) =>
            item?.hiringRequestDto?.hiringRequestId === this.hiringRequestId,
        );

        if (this.selectedHiringRequest) {
          this.jobTitle = this.selectedHiringRequest.hiringRequestDto.jobTitle;
          this.jobDescriptionFilePath =
            this.selectedHiringRequest?.hiringRequestDto?.jobDescriptionPath;
        }
        this.getJobDetails();
      },
      error: (error) => {
        this.globalError.logComponentError(error);
      },
    });
  }

  getJobDetails() {
    this.jobDetails.getJobDetails(this.hiringRequestId).subscribe(
      (res: jobDtlsResponse) => {
        if (Array.isArray(res.data?.candidates)) {
          this.rowData = res.data.candidates;
        } else {
          this.rowData = [];
        }
        this.displayData = this.rowData;
        this.totalCount = this.displayData?.length || 0;
        this.isDataLoaded = true;
      },
      (error) => {
        this.isDataLoaded = true;
        this.globalError.logComponentError(error);
      },
    );
  }

  applyJobDetails(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    this.displayData =
      this.rowData && this.rowData.length
        ? this.searchService.getSearchedData(
            this.rowData,
            this.filterValue?.toLowerCase(),
          )
        : [];
    this.totalCount = this.displayData?.length;
  }

  clearInput() {
    this.displayData = this.rowData;
    this.filterValue = '';
  }

  handleDataFromChild(data: jobDetails[]): void {
    this.displayData = data;
    this.totalCount = this.displayData?.length;
    this.filterValue = '';
  }

  private getFileExtension(filePath: string): string {
    const extension = filePath.split('.').pop()?.toLowerCase() || 'pdf';
    return extension;
  }

  receiveData(data: string, candidateId: string) {
    this.displayData?.forEach((item) => {
      if (item?.candidateId === candidateId) {
        item.status = data;
      }
    });
  }

  job_Description(fileName: string) {
    if (this.selectedHiringRequest) {
      const fileExtension = this.getFileExtension(
        this.selectedHiringRequest.hiringRequestDto.jobDescriptionPath,
      );
      const fullFileName = `${this.selectedHiringRequest.hiringRequestDto.jobTitle}_${fileName}.${fileExtension}`;
      this.fileDownloadService.downloadFile(
        this.selectedHiringRequest.hiringRequestDto.jobDescriptionPath,
        fullFileName,
      );
    }
  }

  downloadFile(filePath: string, fileName: number, fileType: string) {
    const fileExtension = this.getFileExtension(filePath);
    const fileNameFormat = `${fileType}_${fileName}.${fileExtension}`;
    this.fileDownloadService.downloadFile(filePath, fileNameFormat);
  }

  viewProfile() {}

  navigateBack() {
    this.close.emit();
  }

  openUrlNewTab(urlToOpen: string | null | undefined) {
    if (!urlToOpen || typeof urlToOpen !== 'string') {
      this.globalError.logComponentError(
        new HttpErrorResponse({ error: 'Invalid URL' }),
      );
      return;
    }
    const url = new URL(
      urlToOpen.startsWith('http') ? urlToOpen : 'http://' + urlToOpen,
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
